import React from 'react';
import {
  CasePlanAddSection,
  CasePlanSection,
  CasePlanSectionSkeleton,
} from '@orijinworks/frontend-commons';
import { CASE_PLAN_SECTION_SKELETON_COUNT } from '../../../../constants/reentry-planning.constants';
import {
  getSectionKey,
  isPendingSection,
  resolveSectionTabConfig,
} from '../../../../utils/case-plan-section.util';
import { CasePlanSectionTabs } from '../CasePlanSectionTabs';
import { EditableCasePlanSection } from '../EditableCasePlanSection';
import { SectionTabPanel } from './CasePlanDetailsSections.styles';

/**
 * Section list of the plan detail view: Active/Archived toggle, the sections of the selected
 * status, and the Add Section controls that mark where a new section is inserted.
 *
 * Every editing prop comes from `sectionApi` (see `useCasePlanSection`). When section writes
 * are not allowed, that object supplies no handlers and the sections render read-only.
 */
export function CasePlanDetailsSections({
  isSectionsLoading,
  collapsedSections = {},
  onToggleCollapse,
  onAddWidget,
  sectionApi = {},
  onWidgetDescriptionBlur,
}) {
  const {
    activeSectionTab,
    activeSectionCount = 0,
    onSectionTabChange,
    visibleSections = [],
    isSectionEditable = false,
    destructiveAction = null,
    newSectionKey = null,
    getSectionMoveBoundaries,
    onAddSection,
    onSectionTitleSave,
    onSectionDescriptionSave,
    onSectionVisibilityChange,
    onSectionMove,
    onSectionRemove,
  } = sectionApi;

  if (isSectionsLoading) {
    return <CasePlanSectionSkeleton count={CASE_PLAN_SECTION_SKELETON_COUNT} />;
  }

  const activeTabConfig = resolveSectionTabConfig(activeSectionTab);

  /**
   * `insertBeforeSectionKey` is the section the new one lands above; null appends after the
   * last section, which is also the empty-plan case. It is a section *key* so a new section
   * can be inserted above one whose own create has not answered yet.
   */
  const renderAddSection = (insertBeforeSectionKey, position) => {
    if (!onAddSection) {
      return null;
    }

    return (
      <CasePlanAddSection
        key={`add-section-${position}`}
        onClick={() => onAddSection(insertBeforeSectionKey)}
      />
    );
  };

  const renderReadOnlySection = (section) => {
    const sectionKey = getSectionKey(section);

    return (
      <CasePlanSection
        key={sectionKey}
        title={section.title}
        description={section.description}
        visibleToLearner={section.visibleToLearner}
        widgets={section.widgets}
        collapsed={collapsedSections[sectionKey] ?? false}
        onToggleCollapse={() => onToggleCollapse(sectionKey)}
      />
    );
  };

  const renderEditableSection = (section) => {
    const sectionKey = getSectionKey(section);
    const isPending = isPendingSection(section);
    const { moveUpDisabled, moveDownDisabled } = getSectionMoveBoundaries(sectionKey);

    return (
      <EditableCasePlanSection
        // The key stays the same when a pending section's temporary id is replaced by its
        // server UUID, so the row the admin is typing in is never remounted mid-edit.
        key={sectionKey}
        section={section}
        collapsed={collapsedSections[sectionKey] ?? false}
        onToggleCollapse={() => onToggleCollapse(sectionKey)}
        // A widget needs a server id to attach to, so the control waits for the create to
        // answer. Omitting the handler is the shared component's way of hiding it entirely.
        onAddWidget={isPending ? undefined : () => onAddWidget(section.id)}
        onWidgetDescriptionBlur={onWidgetDescriptionBlur}
        // Move boundaries already account for pending rows — both the row itself and its
        // neighbours, whose swap target would otherwise be ambiguous.
        moveUpDisabled={moveUpDisabled}
        moveDownDisabled={moveDownDisabled}
        titleAutoFocus={sectionKey === newSectionKey}
        destructiveAction={destructiveAction}
        onTitleSave={onSectionTitleSave}
        onDescriptionSave={onSectionDescriptionSave}
        onVisibilityChange={onSectionVisibilityChange}
        onMove={onSectionMove}
        onRemove={onSectionRemove}
      />
    );
  };

  const renderSection = (section) =>
    isSectionEditable ? renderEditableSection(section) : renderReadOnlySection(section);

  const sectionRows = visibleSections.flatMap((section) => [
    renderAddSection(getSectionKey(section), `before-${getSectionKey(section)}`),
    renderSection(section),
  ]);

  return (
    <>
      <CasePlanSectionTabs
        activeSectionTab={activeSectionTab}
        activeSectionCount={activeSectionCount}
        onSectionTabChange={onSectionTabChange}
      />
      <SectionTabPanel
        role="region"
        id={activeTabConfig.panelId}
        aria-labelledby={activeTabConfig.tabId}
      >
        {sectionRows}
        {renderAddSection(null, 'end')}
      </SectionTabPanel>
    </>
  );
}
